import { useEffect, useState } from "react";
import { Droplets, Plus, Trash2 } from "lucide-react";
import { api, money, num, trDate, todayISO } from "../lib/api";
import { PageHeader, Modal, FormField, SubmitButton, useForm, EmptyState, StatMini } from "../components/farm/FarmUI";
import { HERO, HERO_TINT } from "../lib/heroes";

export default function Dairy() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const { form, set, reset } = useForm({ date: todayISO(), liters: "", sale_amount: "", expense_amount: "", note: "" });

  const load = () => api.get("/dairy").then(setItems).catch(() => setError("Kayıtlar yüklenemedi."));
  useEffect(() => { load(); }, []);

  const totalLiters = items.reduce((a, r) => a + (Number(r.liters) || 0), 0);
  const totalSale = items.reduce((a, r) => a + (Number(r.sale_amount) || 0), 0);
  const totalExpense = items.reduce((a, r) => a + (Number(r.expense_amount) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      date: form.date,
      liters: Number(form.liters) || 0,
      sale_amount: Number(form.sale_amount) || 0,
      expense_amount: Number(form.expense_amount) || 0,
      note: form.note,
    };
    try {
      const rec = await api.post("/dairy", payload);
      setItems((x) => [rec, ...x].sort((a, b) => (b.date || "").localeCompare(a.date || "")));
      reset();
      setOpen(false);
    } catch {
      setError("Kayıt eklenemedi.");
    }
  };

  const remove = async (id) => {
    try { await api.del(`/dairy/${id}`); setItems((x) => x.filter((i) => i.id !== id)); }
    catch { setError("Silinemedi."); }
  };

  return (
    <>
      <PageHeader
        eyebrow="SÜT İŞLETMESİ · GÜNLÜK"
        title="Süt kayıtları"
        subtitle="Günlük süt üretimi, satış ve gider takibi."
        heroImage={HERO.dairy}
        heroTint={HERO_TINT.dairy}
        actions={
          <button className="primary-btn" data-testid="dairy-add-btn" onClick={() => setOpen(true)}>
            <Plus size={17} /> Günlük kayıt
          </button>
        }
      />

      {error && <div className="api-error" data-testid="dairy-error">{error}</div>}

      <section className="stats-mini-row">
        <StatMini label="Kayıt sayısı" value={`${items.length}`} tone="teal" testId="dairy-stat-count" />
        <StatMini label="Toplam süt" value={`${num(totalLiters)} lt`} tone="teal" testId="dairy-stat-liters" />
        <StatMini label="Satış" value={money(totalSale)} tone="green" testId="dairy-stat-sale" />
        <StatMini label="Gider" value={money(totalExpense)} tone="red" testId="dairy-stat-expense" />
        <StatMini label="Net" value={money(totalSale - totalExpense)} tone="green" testId="dairy-stat-net" />
      </section>

      <div className="panel">
        {items.length === 0 ? (
          <EmptyState icon={Droplets} title="Henüz süt kaydı yok" text="Her gün topladığınız süt miktarını, satış ve giderleri buraya girin." actionLabel="İlk kaydı ekle" onAction={() => setOpen(true)} testId="dairy-empty-add" />
        ) : (
          <div className="dairy-table">
            <div className="dairy-head">
              <span>Tarih</span><span>Süt (lt)</span><span>Satış</span><span>Gider</span><span>Net</span><span></span>
            </div>
            {items.map((r) => {
              const net = (Number(r.sale_amount) || 0) - (Number(r.expense_amount) || 0);
              return (
                <div className="dairy-row" key={r.id} data-testid={`dairy-item-${r.id}`}>
                  <span><Droplets size={14} /> {trDate(r.date)}</span>
                  <span>{num(r.liters)}</span>
                  <span className="positive">{money(r.sale_amount)}</span>
                  <span className="negative">{money(r.expense_amount)}</span>
                  <span className={net >= 0 ? "positive" : "negative"}><b>{money(net)}</b></span>
                  <button className="delete-btn always" data-testid={`dairy-delete-${r.id}`} onClick={() => remove(r.id)}><Trash2 size={14} /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {open && (
        <Modal title="Günlük süt kaydı" subtitle="SÜT" onClose={() => setOpen(false)}>
          <form onSubmit={submit}>
            <FormField label="Tarih">
              <input required type="date" data-testid="dairy-form-date" value={form.date} onChange={(e) => set("date", e.target.value)} />
            </FormField>
            <FormField label="Süt miktarı (litre)">
              <input required type="number" step="0.1" data-testid="dairy-form-liters" placeholder="0" value={form.liters} onChange={(e) => set("liters", e.target.value)} />
            </FormField>
            <div className="form-row">
              <FormField label="Satış tutarı (₺)">
                <input type="number" step="1" data-testid="dairy-form-sale" value={form.sale_amount} onChange={(e) => set("sale_amount", e.target.value)} />
              </FormField>
              <FormField label="Gider (₺)">
                <input type="number" step="1" data-testid="dairy-form-expense" value={form.expense_amount} onChange={(e) => set("expense_amount", e.target.value)} />
              </FormField>
            </div>
            <FormField label="Not">
              <textarea data-testid="dairy-form-note" placeholder="Alıcı, yem, veteriner vb." value={form.note} onChange={(e) => set("note", e.target.value)} />
            </FormField>
            <SubmitButton testId="dairy-form-submit">Kaydet</SubmitButton>
          </form>
        </Modal>
      )}
    </>
  );
}
